import {
	Conversation,
	type Figure,
	Figures,
	InShort,
	RelatedPages,
	type Turn,
} from "@marketing/components/answer";
import { Article } from "@marketing/components/article";
import {
	marketingHead,
	PageIntro,
	Section,
	Steps,
} from "@marketing/components/blocks";
import {
	type ExampleRequest,
	ExampleRequests,
} from "@marketing/components/ExampleRequests";
import { FlowDiagram } from "@marketing/components/FlowDiagram";
import { Closing } from "@marketing/home/Closing";
import { Faq } from "@marketing/home/Faq";
import { createFileRoute } from "@tanstack/react-router";

const UPDATED = "2026-11-05";

const FAQ = [
	{
		q: "Does it understand Saudi Arabic and Arabizi?",
		a: "Yes. It reads Saudi Arabic as people write it to a shop, Najdi and Hijazi wording included, Arabic typed in Latin letters (Arabizi), English, and messages that mix them. It answers in the language and script it was asked in. On calls it hears and speaks Saudi Arabic with a Saudi voice.",
	},
	{
		q: "Can shoppers order on WhatsApp in Saudi Arabia?",
		a: "Yes. Your own WhatsApp Business number answers with product cards, builds the basket and sends a checkout the shopper confirms with a tap. Shoppers can also send voice notes or call the number.",
	},
	{
		q: "Are prices in riyals and per branch?",
		a: "Yes. Each branch keeps its own prices and stock in riyals, and the assistant offers only what the shopper's branch can deliver.",
	},
	{
		q: "Are alcohol, pork and tobacco hidden?",
		a: "Yes, when you set the store as Halal. Our server removes those products before the assistant sees the catalog, on every channel, so no wording can bring them back. A product that has not been checked yet stays hidden.",
	},
	{
		q: "Where is the data stored, and how does it handle the PDPL?",
		a: "Our servers are in the European Union today, and no conversation is ever used to train an AI model. We do not claim PDPL certification. We go through your requirements under Saudi Arabia's Personal Data Protection Law with your team before launch.",
	},
	{
		q: "Does it work with Salla or Zid?",
		a: "Yes. Salla and Zid stores connect directly: products and variants in Arabic and English, stock per branch or location, and categories, kept current by the platform's webhooks and a daily refresh. Shopify, WooCommerce, product feeds, our REST API and custom connectors work too.",
	},
	{
		q: "Which Saudi retailers use it?",
		a: "Access is by invitation and we name retailers only with their permission. The team behind Hephon Commerce built Panda's in-app Saudi-dialect shopping assistant, a separate build described in the Panda case study on hephon.ai.",
	},
	{
		q: "How long does it take to go live?",
		a: "With a Salla, Zid, Shopify or WooCommerce store, or a product feed, a first assistant on your catalog is usually ready in days. A custom connector to your own platform takes longer; we scope it with you.",
	},
	{
		q: "How is it priced?",
		a: "Pricing is a proposal per retailer and depends on your stores and the channels you use. There is no public price. Book a demo and we will send you a proposal.",
	},
];

const REQUESTS: ExampleRequest[] = [
	{
		text: "أبغى رز بسمتي خمسة كيلو",
		en: "I want five kilos of basmati rice.",
	},
	{ text: "وش أرخص حليب عندكم؟", en: "What is your cheapest milk? (Najdi)" },
	{
		text: "أبي أسوي كبسة لعشرة أشخاص، وش أحتاج؟",
		en: "I want to make kabsa for ten people, what do I need?",
	},
	{
		text: "إيش عندكم سناكات للمدرسة بأقل من خمسين ريال؟",
		en: "What school snacks do you have under fifty riyals? (Hijazi)",
	},
	{
		text: "abgha 7aleeb almarai kbeer",
		en: "I want the large Almarai milk (Arabizi).",
	},
	{
		text: "فيه شي مشابه بس بدون سكر؟",
		en: "Anything similar, but sugar-free?",
	},
];

const CHAT: Turn[] = [
	{
		from: "shopper",
		text: "السلام عليكم، أبي رز بسمتي خمسة كيلو وحليب كامل الدسم",
		en: "Hello, I want five kilos of basmati rice and full-fat milk.",
	},
	{
		from: "assistant",
		text: "وعليكم السلام، أبشر. هذي الخيارات المتوفرة في فرعك:",
		en: "Hello, right away. Here is what your branch has:",
		note: "Two searches at the shopper's branch, one row each, with prices in riyals.",
	},
	{
		from: "shopper",
		text: "إيش أرخص واحد في الرز؟",
		en: "Which rice is the cheapest? (Hijazi wording)",
	},
	{
		from: "assistant",
		text: "رتبتها لك من الأرخص. تبي أضيف الأول للسلة؟",
		en: "I sorted them cheapest first. Shall I add the first one?",
		note: "Same search, cheapest first, only rice that matches the request.",
	},
	{
		from: "shopper",
		text: "ok add it, w abgha asawi kabsa l 10 ashkhas",
		en: "OK add it, and I want to make kabsa for 10 people. (English and Arabizi)",
	},
	{
		from: "assistant",
		text: "tamam, adaftah. hathi aghradh el kabsa l 10, kil wa7ed bil 7ajm elli yekfi:",
		en: "Done, added. Here are the kabsa ingredients for 10, each in the pack size that covers it:",
		note: "Recipe: ingredient lines with amounts, and for each the pack that covers it for the least money. Answered in Arabizi because the shopper wrote in Arabizi.",
	},
	{
		from: "shopper",
		text: "حطها كلها في السلة",
		en: "Put them all in the basket.",
	},
	{
		from: "assistant",
		text: "تم. السلة جاهزة، راجعها واضغط تأكيد الطلب.",
		en: "Done. Your basket is ready: review it and tap confirm.",
		note: "The basket is in sync with the app's cart. Nothing is ordered until the shopper confirms.",
	},
];

const FIGURES: Figure[] = [
	{
		value: "10M+",
		label: "App users at Panda, whose Saudi-dialect assistant our team built",
		source: "Panda case study, hephon.ai/case-studies/panda. Panda's assistant is a separate build, not Hephon Commerce.",
	},
	{
		value: "3 channels",
		label: "Your app, your WhatsApp number and phone calls, one basket",
		source: "Hephon Commerce product, November 2026.",
	},
	{
		value: "Under 1 s",
		label: "For the assistant to reply on a call",
		source: "From the caller's last word to the assistant's first, as stated by Hephon, November 2026.",
	},
	{
		value: "15,000",
		label: "Products in the public demo store you can test",
		source: "Hephon Market at commerce.hephon.ai/c/demo, a real quick-commerce catalog under a neutral name. Prices in US dollars.",
	},
];

const STEPS = [
	{
		title: "Connect your catalog",
		text: "From Salla, Zid, Shopify, WooCommerce, a product feed, our REST API or a custom connector, with each branch's prices in riyals and its stock.",
	},
	{
		title: "Set it up and test it",
		text: "Its name, its Saudi tone and phrasebook, Halal rules and handover. Then a test suite built from your own catalog runs in Saudi Arabic, Arabizi and English.",
	},
	{
		title: "Go live channel by channel",
		text: "Start in your app or on your WhatsApp number, and add phone orders when you are ready.",
	},
];

const RELATED = [
	{
		href: "/arabic-chatbot-for-ecommerce",
		label: "Arabic chatbot for ecommerce",
	},
	{ href: "/channels/whatsapp", label: "WhatsApp ordering" },
	{ href: "/channels/voice", label: "Voice ordering in Arabic" },
	{ href: "/security/pdpl", label: "Saudi PDPL and your data" },
	{ href: "/uae", label: "For the UAE" },
	{ href: "/supermarkets", label: "For supermarkets" },
];

export const Route = createFileRoute("/_marketing/saudi-arabia")({
	head: () =>
		marketingHead({
			title: "AI Shopping Assistant for Saudi Arabia | Hephon Commerce",
			description:
				"AI shopping assistant for Saudi Arabia: an Arabic chatbot for supermarkets that reads Saudi dialect and Arabizi, with riyal prices per branch and WhatsApp.",
			path: "/saudi-arabia",
			breadcrumb: ["Saudi Arabia"],
			faq: FAQ,
			updated: UPDATED,
		}),
	component: SaudiArabiaPage,
});

function SaudiArabiaPage() {
	return (
		<>
			<PageIntro
				kicker="Saudi Arabia"
				title="The AI shopping assistant for supermarkets in Saudi Arabia"
				lead="It understands shoppers the way they write and speak, in Saudi Arabic, Arabizi and English, searches your catalog with each branch's prices in riyals, and fills the basket in your app, on WhatsApp and on the phone."
			>
				<ExampleRequests
					items={REQUESTS}
					className="mx-auto max-w-4xl pb-10 text-start sm:pb-14"
				/>
			</PageIntro>

			<InShort updated={UPDATED}>
				<p>
					Hephon Commerce is an AI shopping assistant for
					supermarkets, grocery delivery and quick-commerce retailers
					in Saudi Arabia. It reads Saudi Arabic as shoppers write and
					speak it, Arabizi and English, searches the retailer's live
					catalog with each branch's prices in riyals, and fills the
					basket in the retailer's own app, on its WhatsApp Business
					number and on phone calls.
				</p>
				<p>
					Nothing is ordered until the shopper confirms, and in a
					Halal store alcohol, pork and tobacco are removed by the
					server before the assistant sees the catalog. It is built by
					Hephon, the team behind Panda's in-app Saudi-dialect
					shopping assistant.
				</p>
			</InShort>

			<Section
				id="chat-title"
				tone="mist"
				title="A Saudi shopper's chat, start to finish."
				lead="Najdi and Hijazi wording, Arabizi and English in one conversation, as people really text a shop."
				center
			>
				<Conversation
					turns={CHAT}
					caption="A sample conversation written for this page to show the behaviour. It is not a transcript of a real shopper."
				/>
			</Section>

			<Article>
				<h2 id="dialect">
					Does it understand Saudi dialect and Arabizi?
				</h2>
				<p>
					Yes. Saudi shoppers do not write to a shop in formal Arabic.
					They write وش عندكم or إيش عندكم, أبي or أبغى, الحين or
					دحين, and they spell brands three ways: Almarai, المراعي,
					almar3i. Many type Arabic in Latin letters with numbers for
					the missing sounds, so "7aleeb" is milk and "3indkom" is "do
					you have". Younger shoppers drop English words into Arabic
					sentences without thinking about it.
				</p>
				<p>
					Hephon Commerce reads all of that as one request. Its
					product search combines keywords and meaning over your live
					catalog, so it works for Arabic script, Arabizi and English,
					and finds "Tomatoes" when the shopper typed "tomato". It
					replies in the language and script the shopper used: Arabic
					to Arabic, Arabizi to Arabizi, English to English.
				</p>
				<p>
					The Saudi tone of its replies does not depend on a model's
					guesswork. Fixed replies come from a phrasebook of approved
					Saudi phrases for each situation, and your team can edit it.
					That is how it says أبشر and تم instead of a stiff
					translation.
				</p>

				<h2 id="not-enough">
					Why is an Arabic chatbot not enough for a supermarket?
				</h2>
				<p>
					Most Arabic chatbots sold in Saudi Arabia answer questions:
					opening hours, delivery areas, order status. A supermarket
					shopper wants something else: the right milk, at their
					branch's price, in the basket. That needs a catalog, not an
					FAQ.
				</p>
				<p>
					Before the assistant shows any product, a fast classifier
					checks each of the top results against what the shopper
					actually asked, and off-topic items are dropped. Ask for
					"snacks for six watching the game" and you get snacks, not
					basketballs. It understands a budget ("under fifty riyals"),
					"cheapest first", "more options", "something similar" (which
					stays in the same category) and follow-ups such as "any of
					these on offer?". It never assumes food: if your store sells
					phones, a shopper asking for a phone gets phones.
				</p>
				<p>
					It also cooks. "Kabsa for ten" becomes a list of ingredients
					with amounts in grams, millilitres or pieces. For each line
					it picks the pack that covers the amount for the least
					money, and how many packs. "Add all" puts the whole list in
					the basket, and the recipe steps are shown too.
				</p>

				<h2 id="riyals">Are prices in riyals and per branch?</h2>
				<p>
					Yes. Each branch is its own store in Hephon Commerce, with
					its own prices and stock in riyals. The shopper sees only
					what their branch can deliver, so the basket they build is
					the basket that arrives. A product that is out of stock at
					one branch is not offered there, even if another branch has
					it.
				</p>

				<h2 id="halal">Is it halal by default?</h2>
				<p>
					You choose the rules, and the server enforces them. A
					retailer can hide alcohol, pork and tobacco, or all three as
					Halal. Those products are removed before the assistant sees
					the catalog, on every channel, so no wording and no clever
					prompt can bring them back. Each product is judged by an AI
					model, not a word list, and a product that has not been
					checked yet stays hidden. On WhatsApp, alcohol and tobacco
					are always excluded.
				</p>

				<h2 id="whatsapp">
					Can shoppers order on WhatsApp in Saudi Arabia?
				</h2>
				<p>
					Yes. Hephon Commerce answers on your own WhatsApp Business
					number, not a shared one. Shoppers get product cards, a
					basket, and a checkout they confirm with a tap. They can
					send voice notes, which are transcribed and answered like
					typed messages, or call the number and order by voice. It is
					the same assistant and the same basket as in your app.
				</p>

				<h2 id="phone">Can shoppers order by phone in Saudi Arabic?</h2>
				<p>
					Yes. On calls it hears Saudi Arabic as people speak it and
					answers with a Saudi voice. It reads the basket back before
					anything is ordered, and the caller confirms on the keypad.
					A caller can always be handed to a person. The voice
					ordering page explains a call step by step.
				</p>
			</Article>

			<Section
				id="numbers-title"
				tone="mist"
				title="What the numbers say."
				lead="Only figures we can stand behind, each with its source."
			>
				<Figures items={FIGURES} />
			</Section>

			<Article>
				<h2 id="pdpl">How does it handle Saudi data and the PDPL?</h2>
				<p>
					Saudi Arabia's Personal Data Protection Law (PDPL), overseen
					by SDAIA, sets rules for how shoppers' personal data is
					collected, used and sent abroad. Here is what Hephon
					Commerce does today, so your legal team can judge it against
					your obligations:
				</p>
				<ul>
					<li>
						No conversation is ever used to train an AI model. Every
						model request is sent with data collection denied.
					</li>
					<li>
						Our servers are in the European Union today, so data is
						processed outside the Kingdom.
					</li>
					<li>Platform credentials are encrypted.</li>
					<li>Shopper data can be deleted on request.</li>
				</ul>
				<p>
					We do not claim PDPL compliance or any ISO or SOC
					certification. We go through your requirements with your
					team before launch. The{" "}
					<a href="/security/pdpl">PDPL page</a> has the detail.
				</p>

				<h2 id="does-not">What it does not do</h2>
				<ul>
					<li>
						It never places an order on its own. The shopper
						confirms with a tap, on a signed checkout page, or with
						keypad 1 after the basket is read back on a call. A
						spoken or typed yes is not enough.
					</li>
					<li>
						It does not invent products, prices or delivery times.
						It offers what your catalog and the shopper's branch
						have.
					</li>
					<li>
						It does not decide what is halal from a prompt. That is
						done on the server, before the assistant sees the
						catalog.
					</li>
				</ul>

				<h2 id="not-fit">When Hephon Commerce is not the right fit</h2>
				<ul>
					<li>
						You have no online catalog. The assistant needs your
						products, prices and stock to work.
					</li>
					<li>
						You only need customer service: order status, returns
						and opening hours. A customer-service platform will cost
						less and fit better.
					</li>
					<li>
						Your data must stay inside Saudi Arabia from day one.
						Our servers are in the European Union today.
					</li>
					<li>
						You want self-serve signup and a public price list.
						Access is by invitation and each retailer gets a
						proposal.
					</li>
				</ul>
			</Article>

			<Section
				id="fits-title"
				tone="mist"
				title="Your catalog in, orders back to your platform."
				center
			>
				<FlowDiagram />
			</Section>

			<Section id="steps-title" title="Live in three steps." center>
				<Steps items={STEPS} />
			</Section>

			<Faq items={FAQ} title="Saudi retailers ask." />
			<RelatedPages links={RELATED} />
			<Closing />
		</>
	);
}
